import React, { useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import {
  Box,
  Button,
  CenteredColBox,
  Form,
  Input,
  Link,
} from "../../../common/components";
import { useForm } from "../../../common/hooks/useForm";
import { OnboardingContext } from "../../../context";
import { spacing } from "../../../styles/theme";
import { ROUTES } from "../../../constant/common.constant";
import { OnboardingTitle } from "../Component/OnboardingTitle";

interface Props {
  onNextStep: (email: string, password: string) => void;
  onPreviousStep: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  email: string;
}

const validationSchema = object({
  password: string().required("general.errors.required.password"),
});

export const Login = (props: Props) => {
  const { onNextStep, onPreviousStep, email } = props;
  const { errorMsg } = useContext(OnboardingContext);
  const ref = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const formik = useForm({
    initialValues: {
      email: email,
      password: "",
    },
    onSubmit: (values) => {
      onNextStep(values.email, values.password);
    },
    validationSchema: validationSchema,
  });

  return (
    <Box>
      <OnboardingTitle title={t("onboard.welcome")} />
      <Form formik={formik}>
        <CenteredColBox
          gap={{ xs: spacing.groupingMobile, sm: spacing.groupingDesktop }}
          fullWidth
        >
          <Input
            id="email"
            disabled
            fullWidth
            name="email"
            autoComplete="off"
            link={
              <Link onClick={onPreviousStep} smallFont>
                {t("sign_in.change_email")}
              </Link>
            }
            label={t("general.email")}
            value={formik.values.email}
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          <Input
            type="password"
            autoComplete="current-password"
            id="password"
            name="password"
            autoFocus
            fullWidth
            inputRef={ref}
            label={t("sign_in.password")}
            error={errorMsg || undefined}
            showPasswordToggle
            link={
              <Link
                to={ROUTES.RESET_PASSWORD}
                smallFont
                data-test="login.forgotPassword"
              >
                {t("sign_in.forgot_password")}
              </Link>
            }
            data-test="login.password"
          />
        </CenteredColBox>
        <Box
          display="flex"
          justifyContent="center"
          mt={{ xs: spacing.regularMobile, sm: spacing.regularDesktop }}
          fullWidth
        >
          <Button type="submit" fullWidth data-test="login.submitButton">
            {t("sign_in.continue")}
          </Button>
        </Box>
      </Form>
    </Box>
  );
};
